// src/blogs/PostThemeBackdrop.jsx
// Full-bleed layer mounted behind everything else in BlogShell, rendering
// whichever post is currently active's `theme` (see the shape note atop
// blogData.js). Three states, depending on what that post's theme sets:
//   - no theme / no theme.bgImage / theme.scene -> renders nothing, the
//     blog's flat --blog-bg (set on .blog-shell itself) shows through.
//   - theme.bgImage -> crossfades in a 2D image over two alternating layers,
//     since `background-image` can't be transitioned directly.
//   - theme.scene -> mounts a persistent R3F <Canvas> hosting whichever
//     scene that id names in postScenes/index.js. The canvas itself isn't
//     torn down between posts that both set a scene (just its child scene
//     component swaps), so switching between two 3D themes doesn't pay for
//     a fresh WebGL context each time. Scenes receive { accent, theme, post }:
//     the merged theme, and the active post itself (null if none), for scenes
//     that draw something from the post — e.g. HoloClothScene prints its text.
//
// Known simplification: switching between two different theme.scene ids (or
// from one straight to the flat color) currently just swaps/unmounts — no
// crossfade for the 3D case yet, unlike the 2D image path. A true crossfade
// there would need two persistent canvases faded against each other, which
// is worth doing once real 3D per-post scenes exist to justify it.
import React, { Suspense, useState } from "react";
import { Canvas } from "@react-three/fiber";

import { POST_SCENES } from "./postScenes/index.js";
import "./PostThemeBackdrop.css";

export default function PostThemeBackdrop({ theme, post = null }) {
  const SceneComponent = theme?.scene ? POST_SCENES[theme.scene] : null;
  const bgImage = !SceneComponent ? theme?.bgImage ?? null : null;

  // Two alternating layers so a changing bgImage crossfades rather than
  // snapping straight to the new one. Adjusted during render (React's own
  // recommended pattern for resetting/deriving state off a changed prop)
  // rather than in an effect, so the swap lands in the same commit as the
  // prop change instead of one render behind it.
  const [layers, setLayers] = useState([bgImage, null]);
  const [frontIndex, setFrontIndex] = useState(0);

  if (bgImage !== layers[frontIndex]) {
    const nextFrontIndex = frontIndex === 0 ? 1 : 0;
    const nextLayers = [...layers];
    nextLayers[nextFrontIndex] = bgImage;
    setLayers(nextLayers);
    setFrontIndex(nextFrontIndex);
  }

  return (
    <div className="post-theme-backdrop" aria-hidden="true">
      {layers.map((url, i) => (
        <div
          key={i}
          className={`post-theme-backdrop__layer ${
            i === frontIndex && url ? "post-theme-backdrop__layer--visible" : ""
          }`}
          style={url ? { backgroundImage: `url(${url})` } : undefined}
        />
      ))}

      {SceneComponent && (
        <div className="post-theme-backdrop__scene">
          <Canvas dpr={[1, 1.5]} camera={{ position: [0, 0, 5], fov: 45 }}>
            <Suspense fallback={null}>
              <SceneComponent accent={theme?.accent} theme={theme} post={post} />
            </Suspense>
          </Canvas>
        </div>
      )}
    </div>
  );
}
